/**
 * Escapes HTML special characters to prevent XSS injection
 * @param text Untrusted text to escape
 * @returns Escaped text safe for HTML insertion
 *
 * @example
 * escapeHtml('<script>alert("xss")</script>')
 */
export const escapeHtml = (text: string): string => {
  const htmlEscapes: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };

  return text.replace(/[&<>"'/]/g, (char) => htmlEscapes[char]);
};

/**
 * Sanitizes text for use in HTML attributes
 * More aggressive escaping for attribute contexts
 * @param text Text to sanitize for attribute usage
 * @returns Sanitized text safe for HTML attributes
 */
export const sanitizeAttribute = (text: string): string =>
  // Remove any potential attribute injection attempts
  escapeHtml(text).replace(/[\n\r\t]/g, ' ');

/**
 * Creates safe HTML for POI info window content for display on map
 * Escapes user-provided data while preserving structure
 * @param title POI title (user-provided, needs escaping)
 * @param kmFromStart Distance from start in km
 * @param altitude Altitude in meters (optional)
 * @param isStart Whether this is the start point
 * @returns Safe HTML string
 */
export const createSafePOIContent = (
  title: string,
  kmFromStart: number,
  altitude: number | undefined,
  isStart: boolean,
): string => {
  const safeTitle = escapeHtml(title);
  const distanceText = isStart ? 'Start' : `${kmFromStart}km`;
  const altitudeText = altitude ? ` - ${altitude}moh.` : '';

  return `
    <div>
      <b>${safeTitle}</b>
      <p>${distanceText}${altitudeText}</p>
    </div>
  `;
};
